import { sanitizeError, sanitizeRequest } from './redact'
import type { RequestDiagnosticInput, SafeRequestDiagnostic } from './redact'

type Timed<T> = T & { at: string }
export interface ReportDiagnosticSnapshot {
  js_errors: Timed<{ name: string; message: string }>[]
  failed_requests: Timed<SafeRequestDiagnostic>[]
  actions: Timed<{ id: string }>[]
}
interface BufferOptions {
  now?: () => number
  capacity?: Partial<{ errors: number; requests: number; actions: number }>
  actions?: readonly string[]
  requestTemplates?: readonly string[]
}
const MAX_AGE_MS = 600_000
const empty = (): ReportDiagnosticSnapshot => ({ js_errors: [], failed_requests: [], actions: [] })
const limit = (requested: number | undefined, maximum: number) =>
  requested !== undefined && Number.isFinite(requested)
    ? Math.max(0, Math.min(maximum, Math.floor(requested))) : maximum

/** Memory only. Epochs bind async request results to the context where they began. */
export function createReportBuffer(options: BufferOptions = {}) {
  const now = options.now ?? Date.now
  const actions = new Set(options.actions ?? [])
  const templates = new Set(options.requestTemplates ?? [])
  const capacity = {
    js_errors: limit(options.capacity?.errors, 5),
    failed_requests: limit(options.capacity?.requests, 5),
    actions: limit(options.capacity?.actions, 20),
  }
  let data = empty()
  let context: string | null = null
  let enabled = false
  let destroyed = false
  let generation = 0
  const reset = () => { data = empty(); generation += 1 }
  const valid = (epoch: number) => enabled && !destroyed && context !== null && epoch === generation
  function prune(time: number) {
    const current = (item: { at: string }) => {
      const age = time - Date.parse(item.at)
      return age >= 0 && age < MAX_AGE_MS
    }
    data.js_errors = data.js_errors.filter(current)
    data.failed_requests = data.failed_requests.filter(current)
    data.actions = data.actions.filter(current)
  }
  function push<K extends keyof ReportDiagnosticSnapshot>(key: K, item: ReportDiagnosticSnapshot[K][number], epoch: number) {
    if (!valid(epoch)) return
    const time = now()
    if (!Number.isFinite(time) || Math.abs(time) > 8.64e15) return
    prune(time)
    const entries = data[key] as ReportDiagnosticSnapshot[K][number][]
    entries.push({ ...item, at: new Date(time).toISOString() })
    entries.splice(0, Math.max(0, entries.length - capacity[key]))
  }
  return {
    setContext(key: string | null, active: boolean) {
      const next = Boolean(active && key && !destroyed)
      if (context !== key || enabled !== next) reset()
      context = key
      enabled = next
    },
    epoch: () => generation,
    reset,
    destroy() { reset(); enabled = false; destroyed = true; context = null },
    pushError(input: { name?: unknown; message?: unknown }, epoch = generation) {
      if (valid(epoch)) push('js_errors', { ...sanitizeError(input.name, input.message), at: '' }, epoch)
    },
    pushRequest(input: RequestDiagnosticInput, epoch = generation) {
      if (!valid(epoch)) return
      const safe = sanitizeRequest(input, templates)
      if (safe) push('failed_requests', { ...safe, at: '' }, epoch)
    },
    pushAction(input: { id: string; at?: number }, epoch = generation) {
      if (actions.has(input.id) && input.id.length <= 160 && /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/.test(input.id)) {
        push('actions', { id: input.id, at: '' }, epoch)
      }
    },
    snapshot(): ReportDiagnosticSnapshot {
      if (!valid(generation)) return empty()
      prune(now())
      // Fixed field lengths and hard array caps keep this far below 64 KiB.
      return {
        js_errors: data.js_errors.map(x => ({ ...x })),
        failed_requests: data.failed_requests.map(x => ({ ...x })),
        actions: data.actions.map(x => ({ ...x })),
      }
    },
  }
}
